import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '../../store/store';
import * as notifsDb from '../../services/db/notifications';
import type { NotificationItem } from '../../interfaces/maestra';
import { supabase } from '../../lib/supabase';
import type { Avisar, Ir } from '../porta';

// AS NOTIFICAÇÕES DA CONTA — a lógica do antigo `pages/Notifications`, sem o desenho: a primeira
// página, "carregar mais", o realtime (a lista viva enquanto a tela está aberta), marcar como
// lida, marcar todas, limpar e abrir (marca lida e segue o link interno).

export interface GrupoDeNotificacoes {
  artistId: string;
  artistName: string;
  notifications: NotificationItem[];
}

/**
 * Agrupa por artista; o que não tem artista vai para "Geral", que vem primeiro. Os grupos seguem a
 * notificação mais recente de cada um (a API já devolve a mais recente primeiro).
 */
export function agruparPorArtista(items: NotificationItem[], artistNames: Record<string, string>): GrupoDeNotificacoes[] {
  const groups: Record<string, NotificationItem[]> = {};
  items.forEach((item) => {
    const key = item.artist_id || '__general__';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  const entries = Object.entries(groups);
  entries.sort(([keyA, notesA], [keyB, notesB]) => {
    if (keyA === '__general__') return -1;
    if (keyB === '__general__') return 1;
    const latestA = notesA[0]?.created_at || '';
    const latestB = notesB[0]?.created_at || '';
    return latestB.localeCompare(latestA);
  });
  return entries.map(([key, notifications]) => ({
    artistId: key,
    artistName: key === '__general__' ? 'Geral' : artistNames[key] || 'Artista desconhecido',
    notifications,
  }));
}

/**
 * `onde`: a página (/notifications) ou o painel do sino na casca. Cada um tem o próprio canal ao
 * vivo: com a página aberta e o painel aberto por cima, o mesmo nome de canal seria um só.
 */
export const useNotificacoes = (
  { ir: navigate, avisar: message }: { ir: Ir; avisar: Avisar },
  onde: 'pagina' | 'painel' = 'pagina',
) => {
  const user = useAppSelector((s) => s.auth.user);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [artistNames, setArtistNames] = useState<Record<string, string>>({});
  // A primeira página não carregou: a tela diz isso (com "tentar de novo"), e não "nenhuma".
  const [falhou, setFalhou] = useState(false);

  const loadFirstPage = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setFalhou(false);
    setPage(0);
    try {
      const result = await notifsDb.listNotificationsPaginated(user.id, 0);
      setItems(result.items);
      setHasMore(result.hasMore);
      const artistIds = Array.from(new Set(result.items.map((n) => n.artist_id).filter(Boolean) as string[]));
      setArtistNames(artistIds.length ? await notifsDb.fetchArtistNames(artistIds) : {});
    } catch {
      setFalhou(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  // A lista viva enquanto a tela está aberta. A RLS limita os eventos ao próprio usuário.
  useEffect(() => {
    if (!user?.id) return undefined;
    const channel = supabase
      .channel(`notifications-${onde === 'painel' ? 'panel' : 'page'}:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        async (payload) => {
          const notification = payload.new as NotificationItem;
          setItems((prev) => (prev.some((item) => item.id === notification.id) ? prev : [notification, ...prev]));
          if (notification.artist_id) {
            const names = await notifsDb.fetchArtistNames([notification.artist_id]);
            setArtistNames((prev) => ({ ...prev, ...names }));
          }
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, onde]);

  const loadMore = useCallback(async () => {
    if (!user?.id || loadingMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const result = await notifsDb.listNotificationsPaginated(user.id, nextPage);
      setItems((prev) => [...prev, ...result.items]);
      setHasMore(result.hasMore);
      setPage(nextPage);
      const existingIds = new Set(Object.keys(artistNames));
      const newArtistIds = Array.from(new Set(
        result.items.map((n) => n.artist_id).filter((id): id is string => !!id && !existingIds.has(id)),
      ));
      if (newArtistIds.length) {
        const names = await notifsDb.fetchArtistNames(newArtistIds);
        setArtistNames((prev) => ({ ...prev, ...names }));
      }
    } catch {
      message.error('Erro ao carregar mais notificações');
    } finally {
      setLoadingMore(false);
    }
  }, [user?.id, page, loadingMore, artistNames]);

  const markRead = async (id: string) => {
    try {
      await notifsDb.markAsRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      message.error('Não foi possível marcar a notificação como lida');
    }
  };

  const markAll = async () => {
    if (!user?.id) return;
    try {
      await notifsDb.markAllAsRead(user.id);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      message.error('Não foi possível marcar as notificações como lidas');
    }
  };

  // A confirmação é da tela (um modal de perigo): aqui só se limpa.
  const clearAll = async () => {
    if (!user?.id) return;
    try {
      await notifsDb.clearNotifications(user.id);
      setItems([]);
      setArtistNames({});
      setHasMore(false);
    } catch {
      message.error('Não foi possível limpar as notificações');
    }
  };

  const abrir = async (notification: NotificationItem) => {
    if (!notification.read) await markRead(notification.id);
    // Só caminho interno: "//host", barra invertida e espaço (o navegador apaga tab e quebra de
    // linha, e "/\t/host" vira "//host") podem apontar para fora.
    if (notification.link && /^\/(?!\/)[^\s\\]*$/.test(notification.link)) navigate(notification.link);
  };

  const grupos = useMemo(() => agruparPorArtista(items, artistNames), [items, artistNames]);

  return {
    carregando: loading && !items.length,
    falhou: !loading && falhou && !items.length,
    vazio: !loading && !falhou && !items.length,
    tentarDeNovo: () => { void loadFirstPage(); },
    grupos,
    temMais: hasMore,
    carregandoMais: loadingMore,
    naoLidas: items.filter((n) => !n.read).length,
    carregarMais: () => { void loadMore(); },
    marcarTodas: () => { void markAll(); },
    limpar: () => { void clearAll(); },
    abrir: (n: NotificationItem) => { void abrir(n); },
  };
};
